import { Modal } from 'antd';
import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Button } from 'components/atoms/button';
import { Form } from 'components/atoms/form';
import { Select } from 'components/atoms/select';
import { useTranslate, useTranslation } from 'hooks/use-translation';

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const AtomSelect = styled(Select)`
  min-width: 120px;
`;
const Error = styled.div`
  color: var(--red-60);
  font-size: 12px;
  margin-top: calc(-1 * var(--spacing-05));
`;

interface ComponentProps {
  onShiftDays: (shiftBy: number) => void;
  onCancel: () => void;
  visible: boolean;
  modalTitle: string;
  dayOptions?: ShiftDayOption[];
  unitOptions?: ShiftDayOption[];
  deltaOptions?: ShiftDayOption[];
  lowerLimit?: number;
  upperLimit?: number;
}

interface ShiftDayOption {
  value: number;
  label: string;
}

const getDefaultDayOptions = (): ShiftDayOption[] => {
  return [
    { value: 1, label: '1' },
    { value: 2, label: '2' },
    { value: 3, label: '3' },
    { value: 4, label: '4' },
    { value: 5, label: '5' },
    { value: 6, label: '6' },
    { value: 7, label: '7' },
    { value: 8, label: '8' },
    { value: 9, label: '9' },
    { value: 10, label: '10' },
  ];
};
const getDefaultUnitOptions = (translate): ShiftDayOption[] => {
  const dayText = translate('day');
  const weekText = translate('week');
  const monthText = translate('month');

  return [
    { label: dayText, value: 1 },
    { label: weekText, value: 7 },
    { label: monthText, value: 30 },
  ];
};

const getDefaultDeltaOptions = (translate): ShiftDayOption[] => {
  const earlierText = translate(
    'task_list.bulk_editor.options.shift_dates.direction.earlier',
  );
  const laterText = translate(
    'task_list.bulk_editor.options.shift_dates.direction.later',
  );

  return [
    { label: laterText, value: 1 },
    { label: earlierText, value: -1 },
  ];
};

export const ShiftDayModal: FC<ComponentProps> = ({
  onShiftDays,
  onCancel,
  visible,
  modalTitle,
  lowerLimit = 0,
  upperLimit = Number.MAX_SAFE_INTEGER,
}) => {
  const translate = useTranslate();
  const dayOptions = getDefaultDayOptions();
  const unitOptions = getDefaultUnitOptions(translate);
  const deltaOptions = getDefaultDeltaOptions(translate);
  const errorText = useTranslation('templates.shift_modal.shift_days_error');
  const cancelText = useTranslation('cancel');
  const shiftText = useTranslation('templates.shift_modal.shift_days');
  const intialDay = dayOptions[0];
  const intialUnit = unitOptions[0];
  const intialDelta = deltaOptions[0];
  const [selectedDay, setSelectedDay] = useState(intialDay);
  const [selectedUnit, setSelectedUnit] = useState(intialUnit);
  const [selectedDelta, setSelectedDelta] = useState(intialDelta);
  const [errorVisible, setErrorVisible] = useState(false);

  useEffect(() => {
    const shiftBy =
      selectedDay.value * selectedUnit.value * selectedDelta.value;

    setErrorVisible(shiftBy < lowerLimit || shiftBy > upperLimit);
  }, [selectedDay, selectedUnit, selectedDelta, lowerLimit, upperLimit]);

  const clear = () => {
    setSelectedDay(intialDay);
    setSelectedUnit(intialUnit);
    setSelectedDelta(intialDelta);
  };

  const shiftDays = () => {
    onShiftDays(selectedDay.value * selectedUnit.value * selectedDelta.value);
    clear();
  };
  const cancel = () => {
    onCancel();
    clear();
  };

  return (
    <Modal
      title={modalTitle}
      centered
      footer={
        <Footer>
          <Button type="text" onClick={cancel}>
            {cancelText}
          </Button>
          <Button
            data-cy="project-template-phase-shift-day-modal-submit-button"
            type="primary"
            onClick={shiftDays}
            disabled={errorVisible}
          >
            {shiftText}
          </Button>
        </Footer>
      }
      visible={visible}
      width={460}
      onCancel={cancel}
    >
      {visible && (
        <>
          <Form layout="inline">
            <Form.Item data-cy="project-template-phase-shift-day-modal-day-select">
              <AtomSelect
                options={dayOptions}
                value={selectedDay}
                onChange={setSelectedDay}
              />
            </Form.Item>
            <Form.Item data-cy="project-template-phase-shift-day-modal-unit-select">
              <AtomSelect
                options={unitOptions}
                value={selectedUnit}
                onChange={setSelectedUnit}
              />
            </Form.Item>
            <Form.Item data-cy="project-template-phase-shift-day-modal-delta-select">
              <AtomSelect
                options={deltaOptions}
                value={selectedDelta}
                onChange={setSelectedDelta}
              />
            </Form.Item>
          </Form>
          {errorVisible ? <Error>{errorText}</Error> : null}
        </>
      )}
    </Modal>
  );
};
